import React from "react";
import { first, words } from "lodash";
import { Touchable } from "@/shared/ui/Touchable";
import { Card } from "@/shared/ui/Card";
import { AvatarWithName } from "@/shared/ui/Avatar";
import { IconButton } from "@/shared/ui/IconButton";
import { StatusBadge, StatusBadgeProps } from "@/shared/ui/StatusBadge";
import { Column } from "@/shared/ui/Blocks";
import { cn } from "@/shared/utils/shadcn";

interface MemberCardProps {
  name?: string;
  avatarUrl?: string;
  initials?: string;
  onClick?: () => void;
  onRemove?: () => void;
  className?: string;
  status?: StatusBadgeProps;
  disabled?: boolean;
  disabledDescription?: string;
}

interface MemberCardWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  disabledDescription?: string;
}

type MemberCardRemoveProps = {
  children?: React.ReactNode;
  onRemove?: () => void;
  className?: string;
};

export function MemberCardWrapper(props: MemberCardWrapperProps) {
  const {
    onClick,
    children,
    className = "",
    disabled,
    disabledDescription,
    ...rest
  } = props;
  return (
    <Touchable
      {...rest}
      onClick={onClick}
      disabled={disabled}
      tooltip={disabledDescription}
      className={cn(disabled && "cursor-not-allowed opacity-50")}
    >
      <Card
        className={cn(
          "w-[96px] h-[96px] items-center justify-center",
          className
        )}
      >
        {children}
      </Card>
    </Touchable>
  );
}

export function MemberRemoveWrapper(props: MemberCardRemoveProps) {
  const { children, onRemove, className = "" } = props;

  if (!onRemove) {
    return <div className={cn("relative", className)}>{children}</div>;
  }

  return (
    <div className={`relative group ${className}`}>
      {children}
      <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
        <IconButton
          icon="Close"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          size="sm"
          className="bg-red-500 hover:bg-red-600 rounded-full aspect-square p-0"
          iconProps={{ color: "white" }}
        />
      </div>
    </div>
  );
}

export function MemberCard(props: MemberCardProps) {
  const {
    name,
    avatarUrl,
    initials,
    onClick,
    className,
    status,
    disabled,
    disabledDescription,
  } = props;

  console.log("Rendering MemberCard for:", name, "with status:", status);
  return (
    <MemberRemoveWrapper {...props}>
      {status && (
        <Column className="absolute -top-3 self-center left-1/2 -translate-x-1/2 items-center justify-center z-10">
          <StatusBadge label={status.label} variant={status.variant} />
        </Column>
      )}
      <MemberCardWrapper
        onClick={onClick}
        className={className}
        disabled={!!status || disabled}
        disabledDescription={disabledDescription}
      >
        <AvatarWithName
          avatar={avatarUrl}
          initials={initials}
          name={first(words(name))}
        />
      </MemberCardWrapper>
    </MemberRemoveWrapper>
  );
}
